import { useMemo } from 'react';
import { IServerSideGetRowsParams } from 'ag-grid-community';
import { ITagMappingStats } from '@ts/common-types';
import { useDispatch } from 'react-redux';
import { updateBulkPfmMappingCount, updateBulkKeywordMappingCount } from '@features/modalSlice';
import { TAG_MAPPING_STATS_DEFAULT_VALUE } from '@/ts/constants';
import { decompressData } from '@/ts/retrieve-data';
import { getProductMappingList, getKeywordMappingList } from '@/ts/utils';

interface UseMappingDataSourceProps {
    setGridParams: (params: any) => void;
    setGridLoading: (loading: boolean) => void;
    setTagsMappingStats: (stats: ITagMappingStats) => void;
    getMappingStats: (params: any) => Promise<any>;
    getMappingList: (params: any) => Promise<any>;
}

export const useMappingDataSource = ({
    setGridParams,
    setGridLoading,
    setTagsMappingStats,
    getMappingStats,
    getMappingList,
}: UseMappingDataSourceProps) => {
    const dispatch = useDispatch();

    const mappingDataSource = useMemo(() => ({
        getRows: async (params: IServerSideGetRowsParams) => {
            const { startRow, endRow, sortModel, filterModel } = params.request;
            setGridParams({
                startRow,
                endRow,
                sortModel,
                filterModel,
            });
            try {
                setGridLoading(true);
                getMappingStats({
                    startRow,
                    endRow,
                    sortModel,
                    filterModel,
                }).then(statsResponse => {
                    if (statsResponse.status) {
                        setTagsMappingStats(statsResponse.tagMappingStats);
                    }
                }).catch(err => {
                    console.error(err);
                    setTagsMappingStats(TAG_MAPPING_STATS_DEFAULT_VALUE);
                });

                const response = await getMappingList({
                    startRow,
                    endRow,
                    sortModel,
                    filterModel,
                });

                if (response.status) {
                    if (getMappingList === getProductMappingList) {
                        dispatch(updateBulkPfmMappingCount(response.rowCount));
                    } else if (getMappingList === getKeywordMappingList) {
                        dispatch(updateBulkKeywordMappingCount(response.rowCount));
                    }
                    const decompressedRowData = decompressData(response.rows) as any;
                    params.success({
                        rowData: decompressedRowData,
                        rowCount: response.rowCount,
                    });
                } else {
                    params.success({ rowData: [], rowCount: 0 });
                }
            } catch (err) {
                console.error(err);
                params.fail();
            } finally {
                setGridLoading(false);
            }
        },
    }), [setGridParams, setGridLoading, setTagsMappingStats, getMappingStats, getMappingList, dispatch]);

    return mappingDataSource;
}; 